<template>
  <div class="order-book">
    <div class="title">
      <h4>{{ $t("trading.orderbook.title") }}</h4>
    </div>
    <div class="header">
      <div class="col price">{{ $t("trading.table.price") }} (USDT)</div>
      <div class="col">{{ $t("trading.table.size") }} ({{ ticker }})</div>
      <div class="col">{{ $t("trading.orderbook.sum") }} ({{ ticker }})</div>
    </div>

    <div class="asks">
      <div
        v-for="(ask, index) in reversedAsks"
        :key="'ask-' + index"
        class="row"
      >
        <div
          class="depth-bar red"
          :style="{ width: getDepthWidth(ask.sum, maxSum, 'ask') }"
        />
        <div class="col price red">{{ ask.price }}</div>
        <div class="col">{{ formatQuantity(ask.amount) }}</div>
        <div class="col">{{ formatQuantity(ask.sum) }}</div>
      </div>
    </div>

    <div class="current-price">{{ currentPrice }}</div>

    <div class="bids">
      <div v-for="(bid, index) in sortedBids" :key="'bid-' + index" class="row">
        <div
          class="depth-bar green"
          :style="{ width: getDepthWidth(bid.sum, maxSum, 'bid') }"
        />
        <div class="col price green">{{ bid.price }}</div>
        <div class="col">{{ formatQuantity(bid.amount) }}</div>
        <div class="col">{{ formatQuantity(bid.sum) }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useStore } from "vuex";
import { throttle } from "lodash-es";

const store = useStore();
const currentPrice = computed(() => store.state.transact.lastPrice);
const ticker = computed(() => store.state.transact.ticker);
const rawOrderbook = computed(
  () => store.state.transact.orderbookInfo || { bids: [], asks: [] },
);
const displayOrderbook = ref({ bids: [], asks: [] });

const addCumulativeSum = (orders) => {
  let sum = 0;
  return orders.map((order) => {
    sum += order.amount;
    return { ...order, sum: Number(sum.toFixed(3)) };
  });
};

const sortedAsks = computed(() => {
  return addCumulativeSum(
    [...displayOrderbook.value.asks].sort((a, b) => a.price - b.price),
  );
});
const reversedAsks = computed(() => [...sortedAsks.value].reverse());
const sortedBids = computed(() => {
  return addCumulativeSum(
    [...displayOrderbook.value.bids].sort((a, b) => b.price - a.price),
  );
});
const maxSum = computed(() => {
  const askMax = Math.max(...sortedAsks.value.map((a) => a.sum), 0);
  const bidMax = Math.max(...sortedBids.value.map((b) => b.sum), 0);
  return Math.max(askMax, bidMax);
});
const bidMaxSum = computed(() =>
  Math.max(...sortedBids.value.map((b) => b.sum), 0),
);
const askMaxSum = computed(() =>
  Math.max(...sortedAsks.value.map((a) => a.sum), 0),
);

const throttledUpdateOrderbook = throttle(() => {
  displayOrderbook.value = {
    bids: rawOrderbook.value.bids.slice(),
    asks: rawOrderbook.value.asks.slice(),
  };
}, 500);

const getDepthWidth = (sum, max) => {
  const percent = max === 0 ? 0 : (sum / max) * 100;
  return `${percent}%`;
};

const formatQuantity = (value) => {
  if (value < 1_000) return value;
  if (value >= 1_000_000_000)
    return Math.floor((value / 1_000_000_000) * 100) / 100 + "B";
  if (value >= 1_000_000)
    return Math.floor((value / 1_000_000) * 100) / 100 + "M";
  if (value >= 1_000) return Math.floor((value / 1_000) * 100) / 100 + "K";
  return Math.floor(value * 100) / 100;
};

watch(
  rawOrderbook,
  () => {
    throttledUpdateOrderbook();
  },
  { deep: true },
);
</script>

<style lang="scss" scoped>
.order-book {
  width: 300px;
  height: 700px;
  font-family: sans-serif;
  font-size: 12px;
  color: var(--txt-default-color);
  padding: 10px;
  border-radius: 6px !important;
  border: 0.25px solid var(--bs-body-bg) !important;
  background: var(--modal-bg-color) !important;
}

.title {
  h4 {
    text-align: center;
    color: var(--txt-default-color);
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
  }
}

.header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  color: var(--tex-gray-color);
}

.col {
  width: 33%;
  text-align: right;
  position: relative;
  z-index: 1;

  &.price {
    text-align: left;
  }
}

.row {
  position: relative;
  display: flex;
  justify-content: space-between;
  height: 22px;
  line-height: 20px;
  overflow: hidden;
  padding: 0 4px;
}

.depth-bar {
  position: absolute;
  top: 0;
  bottom: 0;
  opacity: 0.2;
  z-index: 0;
}

.depth-bar.red {
  background: var(--minus-color);
  right: 0;
}

.depth-bar.green {
  background: var(--plus-color);
  right: 0;
}

.red {
  color: var(--minus-color);
}

.green {
  color: var(--plus-color);
}

.current-price {
  font-size: 20px;
  text-align: left;
  color: var(--plus-color);
  font-weight: bold;
  margin: 8px 0;
}

@media screen and (max-width: 768px) {
  .order-book {
    background: transparent !important;
  }
}
</style>
